"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Cog, ShieldAlert, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Parts() {
  const shouldReduceMotion = useReducedMotion();
  const easeCurve: [number, number, number, number] = [0.16, 1, 0.3, 1];
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06,
        delayChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: easeCurve
      }
    }
  };
  return <section id="partsbeforeyouneedthem" data-nav="light" className="relative w-full bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 border-b border-border">
      {/* Background subtle technical grid */}
      <div className="pointer-events-none absolute inset-0 opacity-15" style={{
      backgroundImage: "radial-gradient(var(--border) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-5xl">
        {/* Rating Plate Machinery Specification Container */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="relative border border-border bg-card text-card-foreground p-6 sm:p-10 md:p-14 shadow-xs">
          {/* Machined Datum Corner Tick Marks */}
          <span className="absolute -top-1.5 -left-1.5 size-3 border-t-2 border-l-2 border-foreground/70 pointer-events-none" aria-hidden="true" />
          <span className="absolute -top-1.5 -right-1.5 size-3 border-t-2 border-r-2 border-foreground/70 pointer-events-none" aria-hidden="true" />
          <span className="absolute -bottom-1.5 -left-1.5 size-3 border-b-2 border-l-2 border-foreground/70 pointer-events-none" aria-hidden="true" />
          <span className="absolute -bottom-1.5 -right-1.5 size-3 border-b-2 border-r-2 border-foreground/70 pointer-events-none" aria-hidden="true" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left Header Column */}
            <div className="lg:col-span-5 space-y-4">
              <motion.h2 variants={itemVariants} className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground leading-none">
                Think About Parts Before You Need Them
              </motion.h2>

              <motion.div variants={itemVariants} className="pt-2 flex items-center gap-4 text-xs font-mono text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Cog className="size-4 text-primary" />
                  <span>Controllers</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Wrench className="size-4 text-primary" />
                  <span>Switches</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <ShieldAlert className="size-4 text-primary" />
                  <span>Blowers</span>
                </div>
              </motion.div>
            </div>

            {/* Right Narrative & Action Column */}
            <div className="lg:col-span-7 space-y-6">
              <motion.div variants={itemVariants} className="space-y-4 font-sans text-sm sm:text-base leading-relaxed text-foreground/90">
                <p className="border-l-2 border-primary/40 pl-4">
                  A mechanical bull is equipment you may own for years, so replacement parts matter from the beginning. Before buying, consider whether compatible controllers, switches, inflatable components, blowers and other serviceable parts can be sourced for the system.
                </p>

                <p className="border-l-2 border-border pl-4 text-foreground/80">
                  This is especially important for commercial operators because a relatively small unavailable component can create much more expensive downtime.
                </p>

                <p className="border-l-2 border-border pl-4 text-muted-foreground font-mono text-xs sm:text-sm">
                  Mechanical Bull Shop maintains a dedicated Parts & Accessories section for available replacement equipment. Always confirm compatibility with the exact machine before ordering.
                </p>
              </motion.div>

              {/* Action Area */}
              <motion.div variants={itemVariants} className="pt-4">
                <Button asChild className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.99] font-mono text-xs sm:text-sm tracking-wider uppercase h-12 px-6 shadow-none transition-[background-color,transform]">
                  <Link to="/MechanicalBullPartsAndAccessoriesPage" className="inline-flex items-center justify-center gap-2">
                    <span>BROWSE PARTS & ACCESSORIES</span>
                    <ArrowUpRight className="size-4" />
                  </Link>
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}